import { Icon, isIconName, type IconName } from "@openanalyst-ai/ui/icon"
import { createMemo, createSignal, Show } from "solid-js"
import { findBrand } from "@/plugins/brand-icons"

// Every plugin app icon is colorful, of a unique-per-app color. A recognised brand keeps its own
// brand color; everything else gets a stable color from this palette (hashed off the app id, so
// the same app is always the same color). Real product IMAGES are unaffected — color doesn't tint
// an <img>, so branded image icons keep their own artwork.
const APP_ICON_PALETTE = [
  "#0668e1", "#0f9f7a", "#e5730d", "#7c3aed", "#e5484d", "#0891b2",
  "#d6409f", "#65a30d", "#2563eb", "#c2410c", "#0d9488", "#9333ea",
  "#dc2626", "#059669", "#ca8a04", "#0284c7", "#db2777", "#475569",
]

function appIconColor(input: { id?: string; icon: string; title: string; capabilities?: string[] }): string {
  const brand = findBrand(input)
  if (brand) return brand.hex
  const key = (input.id || input.title || input.icon || "").toLowerCase()
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  return APP_ICON_PALETTE[hash % APP_ICON_PALETTE.length]!
}

const iconAliases: Array<{ icon: IconName; tokens: string[] }> = [
  { icon: "meta-ads", tokens: ["meta-ads", "metaads", "meta-advertising", "facebook-ads", "facebook-advertising", "fb-ads"] },
  { icon: "dashboard", tokens: ["dashboard", "dashboards"] },
  { icon: "mail", tokens: ["email", "mail", "inbox", "gmail", "outlook"] },
  { icon: "crm", tokens: ["crm", "sales", "customer", "customers", "pipeline"] },
  { icon: "shopify", tokens: ["shopify", "shopify-store", "shopify-admin"] },
  { icon: "storefront", tokens: ["shop", "store", "storefront", "commerce", "ecommerce", "orders", "products"] },
  { icon: "generative-studio", tokens: ["generative", "image", "video", "lipsync", "cinema"] },
  { icon: "brain", tokens: ["memory", "10x-memory", "10xmemory", "knowledge"] },
  { icon: "ads", tokens: ["ads", "advertising", "campaign", "campaigns", "marketing"] },
  { icon: "analytics", tokens: ["analytics", "reporting", "metrics", "insights", "attribution"] },
  { icon: "document", tokens: ["publishing", "publish", "document", "documents", "content", "reports"] },
  { icon: "receipt-text", tokens: ["receipt", "invoice", "billing"] },
  { icon: "panels-top-left", tokens: ["panels", "workspace-app"] },
  { icon: "share", tokens: ["understand", "project-graph", "grounding", "scan", "graph"] },
  { icon: "users", tokens: ["users", "people", "team", "contacts"] },
  { icon: "activity", tokens: ["activity", "status", "monitor", "lsp"] },
]

function normalizeIconToken(value?: string) {
  return value?.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") ?? ""
}

function iconImageSource(value: string) {
  const icon = value.trim()
  return /^(https?:|data:image\/|blob:)/i.test(icon) ? icon : ""
}

function resolvePluginIcon(input: {
  id?: string
  icon: string
  title: string
  capabilities?: string[]
}) {
  const searchable = [input.id, input.icon, input.title, ...(input.capabilities ?? [])]
    .map(normalizeIconToken)
    .filter(Boolean)
    .join(" ")
  if (searchable.split(" ").some((item) => item.split("-").includes("shopify"))) return "shopify"

  const direct = [input.icon.trim(), normalizeIconToken(input.icon)].filter(isIconName)[0]
  if (direct) return direct

  return iconAliases.find((item) => item.tokens.some((token) => searchable.includes(token)))?.icon ?? "plugin"
}

export function PluginAppIcon(props: {
  id?: string
  icon: string
  title: string
  capabilities?: string[]
  size?: "small" | "normal" | "medium" | "large"
  /** Optional brand SVG path. When provided, renders the brand glyph instead of an icon-name. */
  brandPath?: string
}) {
  const iconName = createMemo(() => resolvePluginIcon(props))
  const imageSource = createMemo(() => iconImageSource(props.icon))
  const [failedSource, setFailedSource] = createSignal("")
  const showImage = () => !!imageSource() && failedSource() !== imageSource()
  const showBrand = () => !!props.brandPath && !showImage()

  return (
    <span
      data-plugin-app-icon
      data-icon-token={showImage() ? "image" : showBrand() ? "brand" : iconName()}
      class="inline-flex size-5 items-center justify-center leading-none"
      style={{ color: appIconColor(props), "--app-icon-color": appIconColor(props) }}
    >
      <Show
        when={showImage()}
        fallback={
          <Show
            when={showBrand()}
            fallback={<Icon name={iconName()} size={props.size ?? "small"} />}
          >
            <svg
              data-plugin-app-brand-icon
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              class="size-full"
              aria-hidden="true"
            >
              <path d={props.brandPath} fill="currentColor" />
            </svg>
          </Show>
        }
      >
        <img
          data-plugin-app-icon-image
          src={imageSource()}
          alt={props.title}
          class="size-5 rounded object-contain"
          onError={() => setFailedSource(imageSource())}
        />
      </Show>
    </span>
  )
}
